/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import Area from '@fusioncharts/charts/src/chart/msarea';
import RadarDataset from '../../dataset/radar';
import axisFactory from '../../factories/radar-axis';
import canvasFactory from '../../factories/polar-canvas';
import { pluckNumber, pluck, chartPaletteStr, preDefStr, UNDEF, POSITION_TOP, pluckFontSizeMaintainUnit } from '@fusioncharts/core/src/lib';

const POLAR = 'polar';
/**
 * Creates radar class
 * @class
 */
class Radar extends Area {
  /**
   * constructor fn
   */
  constructor () {
    super();
    this.registerFactory('canvas', canvasFactory);
    this.registerFactory('axis', axisFactory, ['canvas']);
  }
  configureAttributes (obj) {
    // overriding the user given drawfullareaborder value since
    // it is not valid for radar
    this.config.drawfullareaborder = 1;
    this.config.radarRadius = pluckNumber(obj.chart.radarradius);

    super.configureAttributes(obj);
    this.config.alignLegendWithCanvas = 0;
    // as  no canvas is there as per behaviour of Radar chart
  }
  /**
   * Sets the default configuration
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.axisType = POLAR;
    config.axisValuePadding = 10;
    config.gridLineWidth = 1;
    config.showRadarBorder = 1;
    config.showvalues = 0;
    config.radarBorderThickness = 2;
    config.skipCanvasDrawing = true;
    config.useplotgradientcolor = 0;
    config.plotfillalpha = 50;
  }
  /**
   * Sets dimension of Axis components
   */
  setAxisDimention () {
    this.getChildren('yAxis');
  }
  /**
   * function to parse JSON data for configurations of axis
   * @return {Object} configurations of axes
   */
  _feedAxesRawData () {
    var iapi = this,
      dataObj = iapi.getFromEnv('dataSource'),
      xAxisConf,
      yAxisConf;

    xAxisConf = iapi.getSpecificxAxisConf();
    yAxisConf = iapi.getSpecificyAxisConf();

    xAxisConf.vtrendlines = dataObj.vtrendlines;
    yAxisConf.trendlines = dataObj.trendlines;
    return {
      xAxisConf: [xAxisConf],
      yAxisConf: [yAxisConf]
    };
  }

  /**
   * Returns x-axis configuration for chart.
   * @return {Object} configurations of x-axis.
   */
  getSpecificxAxisConf () {
    var iapi = this,
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      xAxisConf = this.getBasexAxisConf();

    xAxisConf.isReverse = iapi.config.reverseXAxis;
    xAxisConf.axisLineColor = pluck(chartAttrs.xaxislinecolor, chartAttrs.axislinecolor, '#000000');
    xAxisConf.showAlternateGridColor = pluckNumber(chartAttrs.showalternatevgridcolor, 1);
    xAxisConf.numDivLines = chartAttrs.numvdivlines;
    xAxisConf.axisName = chartAttrs.xaxisname;
    xAxisConf.setAdaptiveMin = chartAttrs.setadaptivexmin;
    xAxisConf.showLimits = chartAttrs.showvlimits;
    xAxisConf.showDivLineValues = pluckNumber(chartAttrs.showvdivlinevalues, chartAttrs.showvdivlinevalues);
    xAxisConf.zeroPlaneThickness = chartAttrs.vzeroplanethickness;
    xAxisConf.zeroPlaneAlpha = chartAttrs.vzeroplanealpha;
    xAxisConf.showZeroPlaneValue = chartAttrs.showvzeroplanevalue;
    xAxisConf.showAxisLine = pluckNumber(chartAttrs.showxaxisline, chartAttrs.showaxislines, chartAttrs.drawAxisLines, 0);
    xAxisConf.showRadarBorder = chartAttrs.showradarborder;
    xAxisConf.radarBorderColor = chartAttrs.radarbordercolor;
    xAxisConf.radarBorderThickness = chartAttrs.radarborderthickness;
    xAxisConf.radarBorderAlpha = chartAttrs.radarborderalpha;
    xAxisConf.radarFillColor = chartAttrs.radarfillcolor;
    xAxisConf.radarFillAlpha = chartAttrs.radarfillalpha;
    xAxisConf.radarSpikeColor = chartAttrs.radarspikecolor;
    xAxisConf.radarSpikeThickness = chartAttrs.radarspikethickness;
    xAxisConf.radarSpikeAlpha = chartAttrs.radarspikealpha;
    xAxisConf.radarInLineAlpha = chartAttrs.radarinlinealpha;
    xAxisConf.showTooltip = pluckNumber(chartAttrs.showtooltip, 1);

    return xAxisConf;
  }

  /**
   * Returns y-axis configuration for chart.
   * @return {Object} configurations of y-axis.
   */
  getSpecificyAxisConf () {
    var iapi = this,
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      chartConfig = iapi.config,
      palleteString = chartConfig.is3D ? chartPaletteStr.chart3D : chartPaletteStr.chart2D,
      colorM = iapi.getFromEnv('color-manager'),
      outCanfontSizeWithUnit = pluckFontSizeMaintainUnit(chartAttrs.outcnvbasefontsize, chartAttrs.basefontsize, 10);

    return {
      isVertical: true,
      isReverse: !chartConfig.isInverse,
      isOpposit: false,
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, 'Verdana,sans'),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#? ([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.yaxisnamepadding,
      axisValuePadding: chartAttrs.yaxisvaluespadding,
      axisNameFont: chartAttrs.yaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.yaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.yaxisnamefontsize,
      axisNameFontColor: chartAttrs.yaxisnamefontcolor,
      axisNameFontBold: chartAttrs.yaxisnamefontbold,
      axisNameFontItalic: chartAttrs.yaxisnamefontitalic,
      axisNameBgColor: chartAttrs.yaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.yaxisnamebordercolor,
      axisNameAlpha: chartAttrs.yaxisnamealpha,
      axisNameFontAlpha: chartAttrs.yaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.yaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.yaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.yaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.yaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.yaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.yaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.yaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.yaxisnameborderdashgap,
      axisNameWidth: chartAttrs.yaxisnamewidth,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      rotateAxisName: pluckNumber(chartAttrs.rotateyaxisname, 1),
      divLineColor: pluck(chartAttrs.divlinecolor, colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.divlinealpha, colorM.getColor('divLineAlpha')),
      divLineThickness: pluckNumber(chartAttrs.divlinethickness, 1),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 0)),
      divLineDashLen: pluckNumber(chartAttrs.divlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.divlinedashgap, 2),
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatehgridcolor, 1),
      alternateGridColor: pluck(chartAttrs.alternatehgridcolor, colorM.getColor('altHGridColor')),
      alternateGridAlpha: pluck(chartAttrs.alternatehgridalpha, colorM.getColor('altHGridAlpha')),
      numDivLines: chartAttrs.numdivlines,
      axisMinValue: chartAttrs.yaxisminvalue,
      axisMaxValue: chartAttrs.yaxismaxvalue,
      setAdaptiveMin: chartAttrs.setadaptiveymin,
      adjustDiv: chartAttrs.adjustvdiv,
      labelStep: chartAttrs.yaxisvaluesstep,
      showAxisValues: pluckNumber(chartAttrs.showyaxisvalues, chartAttrs.showyaxisvalue),
      showLimits: pluckNumber(chartAttrs.showyaxislimits, chartAttrs.showlimits, iapi.showLimits),
      showDivLineValues: pluckNumber(chartAttrs.showdivlinevalues, chartAttrs.showdivlinevalue),
      // showZeroPlane: chartAttrs.showzeroplane,
      zeroPlaneColor: chartAttrs.zeroplanecolor,
      zeroPlaneThickness: pluckNumber(chartAttrs.zeroplanethickness, chartAttrs.divlinethickness, 2),
      zeroPlaneAlpha: chartAttrs.zeroplanealpha,
      showZeroPlaneValue: chartAttrs.showzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      axisLineThickness: pluckNumber(chartAttrs.yaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.yaxislinealpha, chartAttrs.axislinealpha, 100),
      axisLineColor: pluck(chartAttrs.yaxislinecolor, chartAttrs.axislinecolor, '#000000'),
      showTooltip: pluckNumber(chartAttrs.showtooltip, 1)
    };
  }

  /**
   * space manager for radar
   */
  _spaceManager () {
    // todo marge _allocateSpace and _spacemanager
    var spaceForActionBar,
      actionBarSpace,
      availableWidth,
      availableHeight,
      iapi = this,
      config = iapi.config,
      chartAttr = iapi.getFromEnv('chart-attrib'),
      // components = iapi.components,
      radarBorderThickness = config.radarBorderThickness,
      legendPosition = config.legendPosition ? config.legendposition.split('-') : [],
      xAxis = iapi.getChildren('xAxis') && iapi.getChildren('xAxis')[0],
      yAxisArr = (iapi.getChildren('yAxis') && iapi.getChildren('yAxis')) || [], // eslint-disable-line good-practices/no-single-usage-variable
      yAxis = yAxisArr[0],
      legend = iapi.getChildren('legend') && iapi.getChildren('legend')[0], // eslint-disable-line good-practices/no-single-usage-variable
      allottedSpace,
      decreaseCanvasBy,
      POSITION_BOTTOM = preDefStr.POSITION_BOTTOM,
      math = Math,
      mathMin = math.min,
      mathMax = math.max,
      mathAbs = math.abs,
      remainingSpace,
      radarRadius;

    config.radarRadius = pluckNumber(chartAttr.radarradius);
    // If Radar radius is user defined
    if (config.radarRadius !== UNDEF) {
      config.radarRadius = config.radarRadius > mathMin(config.canvasHeight, config.canvasWidth) / 2 ? mathMin(config.canvasHeight, config.canvasWidth) / 2 : config.radarRadius;
      config.availableHeight = availableHeight = config.canvasHeight - (config.radarRadius * 2);
      config.availableWidth = availableWidth = config.canvasWidth - (config.radarRadius * 2);
    } else { // Calculate the Radar radius
      config.availableHeight = availableHeight = config.canvasHeight * 0.7;
      config.availableWidth = availableWidth = config.canvasWidth * 0.7;
    }

    // Place legend
    allottedSpace = (legendPosition[0] === POSITION_TOP || legendPosition[0] === POSITION_BOTTOM ? availableHeight : availableWidth) * 0.3;
    allottedSpace = (legendPosition[0] === POSITION_TOP || legendPosition[0] === POSITION_BOTTOM ? availableHeight : availableWidth);
    // No space is allocated for legend drawing in single series charts
    if (((config.hasLegend !== false) && xAxis) && config.showLegend) {
      remainingSpace = legend._manageLegendPosition(allottedSpace);
      remainingSpace.bottom = mathMin(mathMax(remainingSpace.bottom, 0), allottedSpace);
      remainingSpace.right = mathMin(mathMax(remainingSpace.right, 0), allottedSpace);
      remainingSpace.top = mathMin(mathMax(remainingSpace.top, 0), allottedSpace);
      remainingSpace.left = mathMin(mathMax(remainingSpace.left, 0), allottedSpace);
      iapi._allocateSpace(remainingSpace);
    }

    if (xAxis) {
      remainingSpace = xAxis.placeAxis(config.availableWidth, config.availableHeight);
      iapi._allocateSpace(remainingSpace);
    }

    if (yAxis) {
      remainingSpace = yAxis.placeAxis(config.canvasWidth);
    }

    // A space manager that manages the space for the tools as well as the captions.
    remainingSpace = iapi._manageChartMenuBar(config.availableHeight);

    spaceForActionBar = config.availableHeight * 0.225; // eslint-disable-line good-practices/no-single-usage-variable
    actionBarSpace = iapi._manageActionBarSpace && (iapi._manageActionBarSpace(spaceForActionBar) || {}); // eslint-disable-line good-practices/no-single-usage-variable
    iapi._allocateSpace(actionBarSpace);

    // For rader make the canvas square with the minimum of width and height
    radarRadius = config.radarRadius > 0 ? config.radarRadius * 2 : mathMin(config.canvasHeight, config.canvasWidth);

    if (config.canvasHeight > radarRadius) {
      decreaseCanvasBy = mathAbs((config.canvasHeight - radarRadius) / 2);
      iapi._allocateSpace({
        top: decreaseCanvasBy,
        bottom: decreaseCanvasBy
      });
    }
    if (config.canvasWidth > radarRadius) {
      decreaseCanvasBy = mathAbs((config.canvasWidth - radarRadius) / 2);
      iapi._allocateSpace({
        left: decreaseCanvasBy,
        right: decreaseCanvasBy
      });
    }
    radarRadius = radarRadius / 2;

    yAxis.setAxisDimention({
      x: config.canvasLeft + config.canvasWidth / 2,
      y: config.canvasTop + config.canvasHeight / 2 - (radarRadius - radarBorderThickness / 2),
      axisLength: radarRadius - (radarBorderThickness / 2)
    });

    xAxis.setAxisConfig({
      radius: radarRadius - (radarBorderThickness / 2)
    });

    xAxis.setAxisDimention({
      x: config.canvasLeft + config.canvasWidth / 2,
      y: config.canvasTop + config.canvasHeight / 2
    });
  }

  /**
   * Sets the name of the component
   * @return {string} name
   */
  static getName () {
    return 'Radar';
  }

  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'Radar';
  }

  /**
   * This method return the dataset definations for this charts
   * @return {string} dataset defination
   */
  getDSdef () {
    return RadarDataset;
  }
}
export default Radar;
